import { type ButtonHTMLAttributes, forwardRef } from "react";
import clsx from "clsx";
import { motion, type HTMLMotionProps } from "framer-motion";

/**
 * FullGerman Button — spec's dark Command Center styling.
 * Five variants, three sizes, motion-on-hover. Default `size="sm"` per
 * design brief ("let buttons be small, not macro").
 */

type Variant = "primary" | "secondary" | "outline" | "ghost" | "surface" | "destructive";
type Size = "lg" | "md" | "sm";

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-[#FFD60A] text-[#0A0A0A] font-bold shadow-[0_4px_20px_rgba(255,214,10,0.25)] hover:bg-[#ffe142] disabled:opacity-50",
  secondary:
    "bg-[#1C1C1E] text-[#FAFAFA] font-medium border border-[rgba(255,255,255,0.12)] hover:bg-[#2C2C2E] hover:border-white/20 disabled:opacity-50",
  outline:
    "bg-transparent border-2 border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10 font-bold disabled:opacity-50",
  ghost:
    "bg-transparent text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/5 font-medium disabled:opacity-50",
  surface:
    "bg-[#E63946] text-[#FAFAFA] font-bold shadow-[0_4px_20px_rgba(230,57,70,0.25)] hover:bg-[#f04b58] disabled:opacity-50",
  destructive:
    "bg-[#FF453A] text-[#FAFAFA] font-bold shadow-[0_4px_20px_rgba(255,69,58,0.25)] hover:bg-[#ff5c52] disabled:opacity-50",
};

const SIZE: Record<Size, string> = {
  lg: "px-4 py-2.5 text-sm rounded-xl gap-2",
  md: "px-3.5 py-1.5 text-xs rounded-lg gap-1.5",
  sm: "px-3 py-1.5 text-xs rounded-lg gap-1.5",
};

const BASE =
  "relative inline-flex items-center justify-center select-none transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed";

interface FgButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const FgButton = forwardRef<HTMLButtonElement, FgButtonProps>(
  function FgButton(
    { variant = "primary", size = "sm", loading, className, disabled, children, leftIcon, rightIcon, ...props },
    ref,
  ) {
    return (
      <motion.button
        ref={ref}
        whileHover={disabled || loading ? {} : { scale: 1.02 }}
        whileTap={disabled || loading ? {} : { scale: 0.96 }}
        transition={{ type: "spring", stiffness: 500, damping: 25 }}
        disabled={disabled || loading}
        className={clsx(BASE, VARIANT[variant], SIZE[size], className)}
        {...(props as HTMLMotionProps<"button">)}
      >
        {/* preserve button dimensions during loading */}
        <span
          className={clsx(
            "inline-flex items-center gap-2 transition-opacity duration-200",
            loading ? "opacity-0" : "opacity-100",
          )}
        >
          {leftIcon}
          {children}
          {rightIcon}
        </span>
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span
              className={clsx(
                "w-4 h-4 rounded-full border-2 border-t-transparent fg-spinner",
                variant === "primary" ? "border-[#0A0A0A]" : "border-[#FFD60A]",
              )}
            />
          </span>
        )}
      </motion.button>
    );
  },
);
